import { parseDate } from '@internationalized/date'

const normalizeDateLocale = (locale?: string) => {
  const resolvedLocale = locale ?? getFormattingLocalePreference('en-GB')

  switch (resolvedLocale) {
    case 'en':
      return 'en-GB'
    case 'nl':
      return 'nl-NL'
    case 'de':
      return 'de-DE'
    default:
      return resolvedLocale
  }
}

type DateFormatOptions = Intl.DateTimeFormatOptions & {
  format?: 'short' | 'numeric'
  includeTime?: boolean
}

const dateFormatOptions: Record<
  NonNullable<DateFormatOptions['format']>,
  Intl.DateTimeFormatOptions
> = {
  short: {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  },
  numeric: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
}

const hasTimeValue = (dateString: string | Date, date: Date) => {
  if (dateString instanceof Date) {
    return [date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()].some(
      Boolean,
    )
  }

  return /(?:T|\s)\d{1,2}:\d{2}/.test(dateString)
}

const appendTimeOptions = (dateString: string | Date, date: Date, options?: DateFormatOptions) => {
  const { format, includeTime = false, ...dateTimeFormatOptions } = options ?? {}

  return {
    ...dateTimeFormatOptions,
    ...(includeTime && hasTimeValue(dateString, date)
      ? {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hour12: false,
        }
      : {}),
  }
}

const formatDate = (dateString?: string | Date, locale?: string, options?: DateFormatOptions) => {
  if (!dateString) return '-'

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) return dateString

  return date.toLocaleDateString(normalizeDateLocale(locale), {
    ...dateFormatOptions[options?.format ?? 'short'],
    ...appendTimeOptions(dateString, date, options),
  })
}

export const dateFormat = formatDate

export const formatToShortDate = (
  dateString?: string | Date,
  locale?: string,
  options?: DateFormatOptions,
) => formatDate(dateString, locale, { ...options, format: 'short' })

export const formatToLongDate = (
  dateString?: string | Date,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
) => {
  if (!dateString) return '-'

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) return dateString

  return date.toLocaleDateString(normalizeDateLocale(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  })
}

export const dateTimeFormat = (dateString?: string | Date, locale?: string) =>
  formatInstant(dateString, normalizeDateLocale(locale), { second: '2-digit' })

// Hours and minutes only, e.g. "10:12" with the default en-GB formatting.
export const formatTimeOfDay = (date: Date, locale?: string) =>
  date.toLocaleTimeString(normalizeDateLocale(locale), { hour: '2-digit', minute: '2-digit' })

export const formatDateCET = (date = new Date()) => {
  const timeZone = 'Europe/Amsterdam'

  const year = date.toLocaleString('en-US', {
    timeZone,
    year: 'numeric',
  })

  const month = date.toLocaleString('en-US', {
    timeZone,
    month: '2-digit',
  })

  const day = date.toLocaleString('en-US', {
    timeZone,
    day: '2-digit',
  })

  const time = date
    .toLocaleTimeString('en-US', {
      timeZone,
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
    })
    ?.split(':')
    ?.join('')

  return `${year}-${month}-${day}_${time}`
}

/** Convert inclusive picker days to local API boundaries; the end is exclusive by default. */
export const createDateRange = (
  dateValues: (string | Date | null)[] | null | undefined,
  endBoundary: 'exclusive' | 'inclusive' = 'exclusive',
) => {
  const toMidnight = (value?: string | Date | null) => {
    if (!value) return undefined
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return undefined
    date.setHours(0, 0, 0, 0)
    return date
  }

  const from = toMidnight(dateValues?.[0])
  const to = toMidnight(dateValues?.[1])
  if (to) {
    // Advance the calendar day so daylight-saving days need not be 24 hours long.
    to.setDate(to.getDate() + 1)
    if (endBoundary === 'inclusive') to.setMilliseconds(-1)
  }

  return {
    from,
    to,
  }
}

export type CreatedDateRangeFilterValue = string[] | Date[] | null | undefined

const parseDateValue = (value?: Date | string | null) => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const toCreatedDateBoundary = (value: Date | string | null | undefined, endOfDay = false) => {
  const date = parseDateValue(value)
  if (!date) return undefined

  const calendarDate = parseDate(
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
  )
  const timeZone = getEffectiveTimeZone()
  const boundaryDate = endOfDay
    ? new Date(calendarDate.add({ days: 1 }).toDate(timeZone).getTime() - 1)
    : calendarDate.toDate(timeZone)

  return boundaryDate.toISOString()
}

export const createCreatedDateRangeQuery = (dateRange?: CreatedDateRangeFilterValue) => {
  if (!Array.isArray(dateRange)) return {}

  const [fromValue, toValue] = dateRange
  return {
    ...(fromValue ? { createdFrom: toCreatedDateBoundary(fromValue) } : {}),
    ...(toValue ? { createdTo: toCreatedDateBoundary(toValue, true) } : {}),
  }
}

/**
 * Converts an API business-date value to the date-only model used by date pickers.
 * ISO strings keep their calendar portion verbatim so timezone offsets cannot
 * move the date to the previous or next day.
 */
export const toBusinessDate = (value?: string | Date | null) => {
  if (!value) return undefined

  if (typeof value === 'string') {
    const dateOnly = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/)?.[1]
    if (!dateOnly) return undefined

    try {
      parseDate(dateOnly)
      return dateOnly
    } catch {
      return undefined
    }
  }

  if (Number.isNaN(value.getTime())) return undefined

  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const isSameDate = (_date1: string | Date, _date2: string | Date) => {
  try {
    const date1 = new Date(_date1)
    const date2 = new Date(_date2)
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  } catch {
    console.error('INVALID DATES', _date1, _date2)
    return false
  }
}
